import { useMemo, useState } from 'react';
import { cn } from '@/utils/styles';
import { ShopTestsPage } from './ShopTestsPage';
import {
  ShopFormulasPage,
  ShopFormulasPageProps,
} from '@screens/ShopPage/ShopFormulasPage';
import { useHasRecommendation } from '@hooks/useHasRecommendation';
import { ShopNoRnrPage } from '@screens/ShopPage/components/ShopNoRnrPage';
import { useGetAllFormulasQuery } from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { KEYS, LocalStorage } from '@utils/LocalStorage.ts';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { ShopLandingPageSkeleton } from './ShopLandingPageSkeleton';
import { EventTracker } from '@/utils';

type Tab = 'formulas' | 'tests';

const TABS: { id: Tab; label: string }[] = [
  { id: 'formulas', label: 'My Formulas' },
  { id: 'tests', label: 'Tests' },
];

export const ShopLandingPage = () => {
  const { data: productData, isLoading, isError } = useGetAllFormulasQuery();
  const localStorageUser = LocalStorage.getItem(KEYS.BOOTUP);
  const { isEligibleForSupplements } =
    localStorageUser && JSON.parse(localStorageUser);

  const visibleTabs = useMemo(() => {
    if (!isEligibleForSupplements) {
      return TABS.filter((tab) => tab.id !== 'formulas');
    }
    return TABS;
  }, [isEligibleForSupplements]);

  const isNoRnrUser = useHasRecommendation();
  const defaultTab = useMemo(() => {
    if (!productData) {
      return undefined;
    }
    const allProductsSubscribed =
      productData.length > 0 &&
      productData.every((formula) => formula.is_subscribed);
    return allProductsSubscribed || !isEligibleForSupplements
      ? 'tests'
      : 'formulas';
  }, [productData, isEligibleForSupplements]);

  const [userSelectedTab, setUserSelectedTab] = useState<Tab>();

  const activeTab = userSelectedTab || defaultTab;
  const [showTabs, setShowTabs] = useState(true);

  const handleCustomizeModeChange: ShopFormulasPageProps['onCustomizeModeChange'] =
    (isCustomizing) => {
      setShowTabs(!isCustomizing);
    };

  if (isLoading || !activeTab) {
    return <ShopLandingPageSkeleton />;
  }

  if (isError) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  return (
    <div className="page-container mx-auto px-4 py-6">
      {showTabs && (
        <div className="flex justify-center mb-8">
          <div className="flex bg-background-secondary rounded-full">
            {(defaultTab === 'tests'
              ? [...visibleTabs].reverse()
              : visibleTabs
            ).map((tab) => (
              <button
                key={tab.id}
                className={cn(
                  'py-2 px-6 rounded-full typography-btn-medium transition-colors',
                  activeTab === tab.id
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-secondary hover:text-primary',
                )}
                onClick={() => {
                  EventTracker.trackEvent(
                    'shop',
                    `tap_shop_home_maintab_my${tab.id}`,
                  );
                  setUserSelectedTab(tab?.id);
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {!isEligibleForSupplements || activeTab === 'tests' ? (
        <ShopTestsPage />
      ) : !isNoRnrUser ? (
        <ShopNoRnrPage />
      ) : (
        <ShopFormulasPage onCustomizeModeChange={handleCustomizeModeChange} />
      )}
    </div>
  );
};
